<template>
  <ModalComponent
    ref="modalRef"
    submitButtonText="Close"
    :hideSubmitButton="true"
    :dismissible="true"
    size="xl"
    @close="reset"
  >
    <template #title>
      <span v-if="selected"
        >ARR {{ periodLabel(selected.year, selected.month) }} ·
        forward-looking</span
      >
    </template>
    <template #body>
      <div class="p-3">
        <LoadingIndicatorComponent
          v-if="isLoading"
          message="Loading companies..."
        />
        <div v-else-if="detail">
          <p class="text-muted small mb-3">
            {{ formatWholeNumber(detail.summary.gwh_total) }} GWh projected
            annual production across {{ detail.summary.device_count }} devices
            of {{ detail.summary.company_count }} companies; the sold part is
            priced per segment
            <span
              v-for="(segment, key) in detail.summary.segment_prices"
              :key="key"
              class="badge bg-light text-muted fw-normal me-1"
            >
              {{ segment.label }}:
              {{ segment.price === null ? "–" : segment.price.toFixed(2) }}
              €/MWh ({{ sourceLabel(segment.source) }})
            </span>
            and contracted forwards at their own price =
            <strong>{{ formatEurOrDash(detail.summary.go_gross_arr) }}</strong>
            GO gross ARR; × each company's fee =
            <strong>{{ formatEurOrDash(detail.summary.go_net_arr) }}</strong> GO
            net ARR (blended
            {{ formatRatio(detail.summary.blended_fee_rate) }}). The company
            table below shows the spot price each company was priced at.
          </p>

          <div class="row g-3 mb-3">
            <div class="col-lg-6">
              <div class="card h-100">
                <div class="card-header py-2">
                  <strong>By technology</strong>
                </div>
                <table class="table table-sm mb-0">
                  <thead class="text-muted small">
                    <tr>
                      <th>Technology</th>
                      <th class="text-end">GWh</th>
                      <th class="text-end">Sold GWh</th>
                      <th class="text-end">Devices</th>
                      <th class="text-end">GO gross ARR</th>
                      <th class="text-end">GO net ARR</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="row in detail.by_technology" :key="row.key">
                      <td class="text-capitalize">{{ row.key }}</td>
                      <td class="text-end">
                        {{ formatWholeNumber(row.annual_mwh / 1000) }}
                      </td>
                      <td class="text-end">
                        {{ formatWholeNumber(row.sold_mwh / 1000) }}
                      </td>
                      <td class="text-end">{{ row.device_count }}</td>
                      <td class="text-end">
                        {{ formatEurOrDash(row.go_gross_arr) }}
                      </td>
                      <td class="text-end fw-semibold">
                        {{ formatEurOrDash(row.go_net_arr) }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <div class="col-lg-6">
              <div class="card h-100">
                <div class="card-header py-2"><strong>By country</strong></div>
                <table class="table table-sm mb-0">
                  <thead class="text-muted small">
                    <tr>
                      <th>Country</th>
                      <th class="text-end">GWh</th>
                      <th class="text-end">Sold GWh</th>
                      <th class="text-end">Devices</th>
                      <th class="text-end">GO gross ARR</th>
                      <th class="text-end">GO net ARR</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="row in detail.by_country" :key="row.key">
                      <td>{{ row.key }}</td>
                      <td class="text-end">
                        {{ formatWholeNumber(row.annual_mwh / 1000) }}
                      </td>
                      <td class="text-end">
                        {{ formatWholeNumber(row.sold_mwh / 1000) }}
                      </td>
                      <td class="text-end">{{ row.device_count }}</td>
                      <td class="text-end">
                        {{ formatEurOrDash(row.go_gross_arr) }}
                      </td>
                      <td class="text-end fw-semibold">
                        {{ formatEurOrDash(row.go_net_arr) }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <h6 class="fw-bold mb-2">
            Companies ({{ detail.companies.length }})
          </h6>
          <DataTable
            :value="detail.companies"
            v-model:filters="filters"
            filterDisplay="row"
            sortField="go_net_arr"
            :sortOrder="-1"
            scrollable
            scrollHeight="460px"
            :virtualScrollerOptions="
              detail.companies.length > 30
                ? { itemSize: 42, numToleratedItems: 20 }
                : undefined
            "
            class="p-datatable-sm border rounded"
          >
            <Column
              field="company_name"
              header="Company"
              sortable
              :showFilterMatchModes="false"
              :showFilterMenu="false"
            >
              <template #body="{ data }">
                <span class="badge text-bg-secondary me-1">{{
                  data.company_id
                }}</span>
                {{ data.company_name }}
                <span v-if="data.country" class="text-muted small ms-1">{{
                  data.country
                }}</span>
              </template>
              <template #filter="{ filterModel, filterCallback }">
                <InputText
                  v-model="filterModel.value"
                  type="text"
                  class="p-column-filter w-100"
                  placeholder="Search"
                  @input="filterCallback()"
                />
              </template>
            </Column>
            <Column
              field="device_count"
              header="Devices"
              sortable
              :style="{ width: '90px' }"
            />
            <Column
              field="annual_mwh"
              header="Annual MWh"
              sortable
              :style="{ width: '200px' }"
            >
              <template #body="{ data }">
                {{ formatWholeNumber(data.annual_mwh) }}
                <div class="small text-muted">
                  <span
                    v-for="(mwh, technology) in data.mwh_by_technology"
                    :key="technology"
                    class="me-2 text-capitalize"
                  >
                    {{ technology }} {{ formatWholeNumber(mwh) }}
                  </span>
                </div>
              </template>
            </Column>
            <Column
              field="price_used"
              header="Spot price"
              sortable
              :style="{ width: '100px' }"
            >
              <template #body="{ data }">{{
                data.price_used === null ? "–" : data.price_used.toFixed(2)
              }}</template>
            </Column>
            <Column
              field="fee_rate"
              header="Fee"
              sortable
              :style="{ width: '140px' }"
            >
              <template #body="{ data }">
                {{ (data.fee_rate * 100).toFixed(1) }}%
                <code class="small text-muted ms-1">{{ data.fee_model }}</code>
              </template>
            </Column>
            <Column
              field="go_gross_arr"
              header="GO gross ARR"
              sortable
              :style="{ width: '150px' }"
            >
              <template #body="{ data }">{{
                formatEurOrDash(data.go_gross_arr)
              }}</template>
            </Column>
            <Column
              field="go_net_arr"
              header="GO net ARR"
              sortable
              :style="{ width: '150px' }"
            >
              <template #body="{ data }">
                <span class="fw-semibold text-info">{{
                  formatEurOrDash(data.go_net_arr)
                }}</span>
                <small
                  v-if="data.go_net_arr !== null && detail.summary.go_net_arr"
                  class="text-muted ms-1"
                  >{{
                    pct(data.go_net_arr ?? 0, detail.summary.go_net_arr ?? 0)
                  }}%</small
                >
              </template>
            </Column>
          </DataTable>
        </div>
      </div>
    </template>
    <template #footer>
      <button class="btn btn-secondary" @click="modalRef?.toggleModal()">
        Close
      </button>
    </template>
  </ModalComponent>
</template>

<script setup lang="ts">
import LoadingIndicatorComponent from "@/components/common/LoadingIndicatorComponent.vue";
import ModalComponent from "@/components/common/ModalComponent.vue";
import { FilterMatchMode } from "@primevue/core/api";
import Column from "primevue/column";
import DataTable, { type DataTableFilterMeta } from "primevue/datatable";
import InputText from "primevue/inputtext";
import { ref, useTemplateRef } from "vue";
import { financialDashboardApi } from "./financialDashboardApi";
import {
  ARR_PRICE_SOURCE_LABELS,
  formatEurOrDash,
  formatRatio,
  formatWholeNumber,
  pct,
  periodLabel,
} from "./financialDashboardMetrics";
import type {
  ArrDetail,
  ArrPriceSource,
  ArrRow,
} from "./financialDashboardTypes";
import { useLatestDetail } from "./useLatestDetail";

/**
 * Drill-down behind one month's forward-looking ARR: the projected volume by technology and
 * country, and every company with its MWh, fee rate and ARR share. Owns its own fetch; open it
 * with `open(row)`. `countryCodes` and `goPrice` must match the filters that produced the series.
 */
const props = defineProps<{
  countryCodes: string[];
  goPrice: number | null;
}>();

const modalRef =
  useTemplateRef<InstanceType<typeof ModalComponent>>("modalRef");
const selected = ref<ArrRow | null>(null);
const { detail, isLoading, load } = useLatestDetail<ArrDetail>();
const filters = ref<DataTableFilterMeta>({
  company_name: { value: null, matchMode: FilterMatchMode.CONTAINS },
});

const sourceLabel = (source: ArrPriceSource): string =>
  ARR_PRICE_SOURCE_LABELS[source];

function open(row: ArrRow) {
  selected.value = row;
  modalRef.value?.toggleModal();
  return load(() =>
    financialDashboardApi.getArrDetail(
      row.year,
      row.month,
      props.countryCodes,
      props.goPrice
    )
  );
}

function reset() {
  selected.value = null;
  detail.value = null;
}

defineExpose({ open });
</script>
